/**
 * FB onboarding funnel container (/fb/onboarding).
 *
 * Standalone acquisition funnel for Facebook-ads traffic (the Landing CTA
 * target), NOT auth-gated. Drives the step config in ./steps.js through the
 * screen renderers in ./components/screens.jsx and tracks each step to PostHog.
 *
 * On the final EMAIL step we create the account BEFORE the paywall (like Keiki),
 * so the user is authenticated through /fb/pricing and payment flows into
 * /success. Two low-friction paths:
 *   - Email: supabase.auth.signUp with a generated password (the parent never
 *     sees it). Needs "Confirm email" turned OFF in Supabase so a session is
 *     returned immediately with no verification email to click.
 *   - Google: one-tap OAuth that returns to /fb/pricing.
 * Either way the funnel answers are stashed in sessionStorage for the paywall.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation, useNavigate } from 'react-router-dom';
import posthog from 'posthog-js';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { trackPixel } from '@/lib/metaPixel';
import { isNative } from '@/lib/platform';
import { startNativeOAuthAndWait } from '@/native/auth';
import { useFbFunnelLang } from '@/lib/useFbFunnelLang';
import { FUNNEL_STEPS, isProgressStep, withName } from './steps';
import { localizeSteps } from './localizeSteps';
import {
  TextScreen,
  BinaryScreen,
  SingleScreen,
  MultiScreen,
  GridScreen,
  CardGridScreen,
  VoiceScreen,
  InterstitialScreen,
  LoadingScreen,
  ScoreScreen,
  EmailScreen,
} from './components/screens';
import { getChrome } from './components/FbShell';

const capture = (event, props) => {
  try {
    posthog.capture(event, props);
  } catch {
    /* analytics is best-effort - never break the funnel */
  }
};

// Stable stand-in for analytics and the pixel while the dev preview harness is
// driving the funnel. Module level so its identity never changes across
// renders, which matters: the abandonment listener closes over it once.
const noop = () => {};

// Anonymous-before-consent (see main.jsx): funnel step events are captured
// anonymously for everyone, but identify() attaches the email to the person and
// so only runs once the visitor has accepted cookies.
const hasAnalyticsConsent = () => {
  try {
    return localStorage.getItem('ttt_cookie_consent') === 'accepted';
  } catch {
    return false;
  }
};

// UTM + referrer for the top-of-funnel event so we can attribute drop-off back
// to a specific ad / campaign in PostHog.
const funnelSource = () => {
  const out = {};
  try {
    const p = new URL(window.location.href).searchParams;
    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach((k) => {
      const v = p.get(k);
      if (v) out[k] = v;
    });
    out.referrer = document.referrer || null;
  } catch {
    /* best-effort */
  }
  return out;
};

// Illustrative "today" score from the parent's own answers. Deliberately lands
// slightly below average so the plan reveal has room to show improvement, and
// is fully deterministic per answer set (no random jitter across renders).
function computeScore(answers) {
  let s = 62;
  const w = answers.words;
  if (w === '50+') s += 14;
  else if (w === '11-50') s += 9;
  else if (w === '6-10') s += 5;
  else if (w === '0') s -= 6;
  else if (w === 'unsure') s += 2;
  if (answers.sentences === 'yes') s += 4;
  if (answers.conversation === 'yes') s += 4;
  if (answers.questions === 'yes') s += 2;
  if (answers.find_words === 'no') s += 2;
  if (Array.isArray(answers.sounds)) s += Math.min(answers.sounds.length, 6);
  return Math.max(38, Math.min(92, s));
}

const RENDERERS = {
  text: TextScreen,
  binary: BinaryScreen,
  single: SingleScreen,
  multi: MultiScreen,
  grid: GridScreen,
  cardgrid: CardGridScreen,
  voice: VoiceScreen,
  interstitial: InterstitialScreen,
  loading: LoadingScreen,
  score: ScoreScreen,
  email: EmailScreen,
};

/**
 * props
 *   previewMode - the code-gated dev harness at /preview is driving this. No
 *                 analytics, no Meta pixel, no Supabase write, no navigation
 *                 away. Rendering only, so a screen can be reviewed safely.
 *   initialStep - seed the step cursor, so /preview?step=7 opens that screen
 *                 directly instead of making the reviewer answer the whole quiz.
 */
export default function FbOnboarding({ previewMode = false, initialStep = 0 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { fbPath, funnelLang } = useFbFunnelLang();
  const { signUp, user } = useAuth();
  // Analytics and the pixel are swapped for no-ops under the harness: a review
  // pass must never land in funnel numbers, and a ghost ViewContent teaches the
  // ad algorithm to buy junk traffic.
  const track = previewMode ? noop : capture;
  const pixel = previewMode ? noop : trackPixel;
  const [index, setIndex] = useState(() => {
    const n = Number(initialStep);
    if (!Number.isFinite(n)) return 0;
    return Math.max(0, Math.min(Math.trunc(n), FUNNEL_STEPS.length - 1));
  });
  const [answers, setAnswers] = useState({});
  const [consent, setConsent] = useState(false);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState(null);
  const startedRef = useRef(false);

  // The Facebook app PRELOADS ad landing pages (hidden webview, plain Safari
  // UA, fbclid intact, JS runs) and discards most of them seconds later. The
  // old visibility-at-mount gate missed most of these: post-fix data (Jul 6-8)
  // still showed 107 "welcome abandons" with a MEDIAN of 2.15s on screen and
  // only 5/166 starts flagged prerendered - i.e. the preloads report a visible
  // state, get scrolled past, and die in ~2s. Those phantoms pollute PostHog
  // AND the Meta pixel (ViewContent on ghosts teaches the ad algorithm to buy
  // more junk, especially Audience Network: 280 starts, 0 completions in 30d).
  //
  // So "engaged" now means PROOF of a human: the first pointer/key interaction,
  // or 2.5s of continuous visibility - whichever comes first. A preload that is
  // never shown, or shown for under 2.5s with no touch, tracks nothing at all.
  const wasHiddenAtMountRef = useRef(
    typeof document !== 'undefined' && document.visibilityState === 'hidden'
  );
  const [engaged, setEngaged] = useState(false);
  useEffect(() => {
    if (engaged) return undefined;
    let dwellTimer = null;
    const engage = () => setEngaged(true);
    const startDwell = () => {
      clearTimeout(dwellTimer);
      dwellTimer = setTimeout(engage, 2500);
    };
    const stopDwell = () => clearTimeout(dwellTimer);
    const onVisibility = () => {
      if (document.visibilityState === 'visible') startDwell();
      else stopDwell();
    };
    if (document.visibilityState === 'visible') startDwell();
    document.addEventListener('visibilitychange', onVisibility);
    // Any real interaction proves a human immediately (passive: never blocks
    // the tap that caused it).
    window.addEventListener('pointerdown', engage, { passive: true });
    window.addEventListener('keydown', engage);
    return () => {
      stopDwell();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pointerdown', engage);
      window.removeEventListener('keydown', engage);
    };
  }, [engaged]);

  // Where a finished funnel lands.
  //
  // Ads traffic MUST keep going straight to /fb/pricing. That page fires the
  // Meta pixel and stashes the purchase payload that /fb/success reads back
  // after Stripe, so slipping another screen in front of it would break
  // conversion tracking on live creatives. Organic traffic (/onboarding) has no
  // pixel contract, so it goes through the discount reveal at /scratch, which
  // then continues to /pricing itself.
  const isAdsFunnel = location.pathname.startsWith('/fb/');
  const afterFunnelPath = isAdsFunnel ? fbPath('/pricing') : '/scratch';

  const steps = useMemo(() => localizeSteps(FUNNEL_STEPS, funnelLang), [funnelLang]);
  const progressSteps = useMemo(() => steps.filter(isProgressStep), [steps]);
  const totalProgress = progressSteps.length;

  // Refs the abandonment listener reads without re-subscribing every step, plus
  // the per-step timer used to measure hesitation (ms spent on each question).
  const stepEnteredAtRef = useRef(Date.now());
  const currentStepRef = useRef(null);
  const answersRef = useRef(answers);
  const completedRef = useRef(false);
  const abandonSentRef = useRef(false);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    if (!engaged || startedRef.current) return;
    startedRef.current = true;
    track('fb_onb_started', {
      total_questions: totalProgress,
      // true = this page was preloaded hidden and later actually shown.
      prerendered: wasHiddenAtMountRef.current,
      ...funnelSource(),
    });
    // Top-of-funnel signal for Meta ad optimization (someone engaged with the
    // funnel). PageView already fired from the base pixel on load.
    pixel('ViewContent', { content_name: 'fb_onboarding' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engaged, totalProgress]);

  const step = steps[index];
  const name = answers.name || '';

  const stepProgressLabel = () => {
    const s = currentStepRef.current;
    if (!s) return null;
    return isProgressStep(s) ? `${progressSteps.indexOf(s) + 1}/${totalProgress}` : `-/${totalProgress}`;
  };

  useEffect(() => {
    if (!step || !engaged) return; // hidden prerender: track nothing until shown
    stepEnteredAtRef.current = Date.now();
    currentStepRef.current = step;
    abandonSentRef.current = false; // new screen = eligible to log a fresh exit
    track('fb_onb_step_viewed', { key: step.key, index, type: step.type });
  }, [step, index, engaged]);

  // Rage-quit / drop-off: when the tab is hidden or the page is being torn down
  // (user swipes the FB in-app browser away, hits back, closes the tab), log
  // exactly which question they were on and how long they sat on it. Guarded so
  // a normal completion (navigate to /fb/pricing) is not counted as an exit, and
  // deduped so a background-then-return only logs once per screen.
  useEffect(() => {
    const logExit = () => {
      if (document.visibilityState === 'visible') return;
      if (completedRef.current || abandonSentRef.current) return;
      const s = currentStepRef.current;
      if (!s) return;
      abandonSentRef.current = true;
      track('fb_onb_abandoned', {
        key: s.key,
        type: s.type,
        progress: stepProgressLabel(),
        ms_on_step: Date.now() - stepEnteredAtRef.current,
        answered: Object.keys(answersRef.current).length,
      });
    };
    document.addEventListener('visibilitychange', logExit);
    window.addEventListener('pagehide', logExit);
    return () => {
      document.removeEventListener('visibilitychange', logExit);
      window.removeEventListener('pagehide', logExit);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  // Stash answers for the paywall (survives the OAuth round-trip and the
  // navigation to /fb/pricing) and tie the PostHog person to the email.
  const persistAnswers = () => {
    const payload = { ...answers, email_consent: consent };
    // The harness writes nothing and identifies nobody.
    if (previewMode) return payload;
    try {
      sessionStorage.setItem('ttt_fb_answers', JSON.stringify(payload));
      // identify() attaches the email (PII) to the PostHog person, so it only
      // runs with cookie consent. Without consent the funnel stays anonymous.
      // The child's real name is never sent (PII) - but the literal word
      // "test" isn't a name, so use it as our own signal to flag internal
      // testing and keep it out of revenue/funnel numbers on the dashboard.
      if (payload.email && hasAnalyticsConsent()) {
        const isTestEntry = (payload.name || '').trim().toLowerCase() === 'test';
        posthog.identify(payload.email, { fb_funnel: true, ...(isTestEntry ? { is_test_signup: true } : {}) });
      }
    } catch {
      /* best-effort */
    }
    return payload;
  };

  // A strong password the parent never sees; the account is effectively
  // passwordless (they return via Google or a password reset if ever needed).
  const genPassword = () =>
    'Ttt-' + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2) + '-9Z';

  // Email path: create the Supabase account, then go to the paywall. With
  // "Confirm email" OFF this returns a session immediately (no verification).
  const finishWithEmail = async () => {
    if (authBusy) return;
    // Harness: the account step renders and the button responds, but no account
    // is created and the reviewer stays on the screen they came to look at.
    if (previewMode) {
      completedRef.current = true;
      return;
    }
    setAuthError(null);
    setAuthBusy(true);
    completedRef.current = true; // reaching the account step is a success, not an exit
    const payload = persistAnswers();
    track('fb_onb_completed', { answered: Object.keys(answers).length, method: 'email' });
    // Already signed in. This happens now that /onboarding is this funnel: an
    // older account whose metadata still says onboarding_completed = false gets
    // sent here by PublicEntryRoute. Creating a second account for them would
    // fail ("already registered"), leave them sessionless, and drop them right
    // back at the landing page on the next visit. Stamp the session we have and
    // send them on instead.
    if (user) {
      try {
        await supabase.auth.updateUser({
          data: {
            onboarding_completed: true,
            child_name: payload.name || null,
            child_gender: payload.gender || null,
          },
        });
      } catch {
        /* non-fatal - the paywall still works */
      }
      try {
        sessionStorage.setItem('ttt_fb_authed', '1');
      } catch {
        /* ignore */
      }
      setAuthBusy(false);
      navigate(afterFunnelPath);
      return;
    }
    const password = genPassword();
    // Same-tab recovery on /fb/success after Stripe (session often drops in the
    // FB in-app browser). Cleared once FbSuccess establishes a live session.
    try {
      sessionStorage.setItem('ttt_fb_pw', password);
    } catch {
      /* ignore */
    }
    try {
      const { data, error } = await signUp(payload.email, password, {
        fb_funnel: true,
        child_name: payload.name || null,
        child_gender: payload.gender || null,
        marketing_consent: !!consent,
      });
      let hasSession = !!data?.session;
      // "already registered" is fine - the account exists; the Stripe webhook
      // maps the subscription by email and they can log in later via Google.
      if (error && !/registered|already/i.test(error.message || '')) {
        console.warn('[fb] signUp error:', error.message);
        track('fb_onb_email_error', { message: error.message || 'unknown' });
      }
      // signUp() doesn't always hand back a live session (some Supabase auth
      // configs only create the row and require a separate sign-in). Without a
      // real session, /fb/success's "go to app" bounces to Landing since
      // ProtectedRoute sees no user - so force one with the password we just
      // set. This is a no-op failure for a genuinely already-registered email
      // (wrong password for that older account), which FbSuccess handles by
      // falling back to /login instead of assuming /home will work.
      if (!hasSession) {
        try {
          const { data: signInData } = await supabase.auth.signInWithPassword({
            email: payload.email,
            password,
          });
          hasSession = !!signInData?.session;
        } catch {
          /* fall through - FbSuccess checks session state itself */
        }
      }
      try {
        sessionStorage.setItem('ttt_fb_authed', hasSession ? '1' : '0');
      } catch {
        /* ignore */
      }
      // Mark onboarding done on the account. Without this the parent is bounced
      // straight back into the funnel every time they open "/" or /login:
      // PublicEntryRoute reads onboarding_completed and sends anyone still false
      // to /onboarding. signUp() hardcodes the flag to false for new accounts, so
      // it has to be set afterwards, and set here rather than through
      // AuthContext.markOnboardingComplete, whose `user` has not caught up with
      // the session we created a moment ago. Not awaited: the paywall should not
      // wait on a round trip, and nothing downstream reads it this second.
      if (hasSession) {
        try {
          supabase.auth
            .updateUser({
              data: {
                onboarding_completed: true,
                child_name: payload.name || null,
                child_gender: payload.gender || null,
              },
            })
            .catch(() => {});
        } catch {
          /* non-fatal */
        }
      }
      // Meta conversion events: Lead (captured a contact) + CompleteRegistration
      // (account created). Both help the ad algorithm optimize.
      pixel('Lead', { content_name: 'fb_signup' });
      pixel('CompleteRegistration', { content_name: 'fb_email', status: true });
    } catch (e) {
      console.warn('[fb] signUp threw:', e);
      track('fb_onb_email_error', { message: (e && e.message) || 'threw' });
    } finally {
      setAuthBusy(false);
      navigate(afterFunnelPath);
    }
  };

  // Google path: one tap, no password, no verification. Returns to whatever
  // afterFunnelPath is (ads: /fb/pricing, organic: /scratch); the client runs
  // with flowType pkce + detectSessionInUrl, so the code exchange completes on
  // whichever page the browser lands on.
  const handleGoogle = async () => {
    if (authBusy) return;
    // Harness: never start a real OAuth redirect out of a preview.
    if (previewMode) {
      completedRef.current = true;
      return;
    }
    setAuthError(null);
    setAuthBusy(true);
    completedRef.current = true; // OAuth redirect away is a success, not an exit
    persistAnswers();
    track('fb_onb_completed', { answered: Object.keys(answers).length, method: 'google' });
    track('login_method_selected', { method: 'google', funnel: 'fb' });
    // Already signed in: no reason to bounce them through Google again. Same
    // reasoning as the email path above.
    if (user) {
      try {
        await supabase.auth.updateUser({ data: { onboarding_completed: true } });
      } catch {
        /* non-fatal */
      }
      setAuthBusy(false);
      navigate(afterFunnelPath);
      return;
    }
    // Fire the lead/registration pixels before the OAuth redirect leaves the page.
    pixel('Lead', { content_name: 'fb_signup' });
    pixel('CompleteRegistration', { content_name: 'fb_google', status: true });
    try {
      // Native shell. This funnel encodes its destination in redirectTo, and a
      // custom scheme deep link carries no destination, so a bare early return
      // would sign the user in and strand them on this screen. Wait for the deep
      // link, then navigate the way the web redirect would have.
      if (isNative()) {
        const r = await startNativeOAuthAndWait('google');
        if (r.handled) {
          if (r.status === 'success') {
            navigate(afterFunnelPath);
          } else {
            track('fb_auth_error', { method: 'google', message: r.error?.message || r.status || 'unknown' });
            setAuthError(getChrome(funnelLang).googleError);
            setAuthBusy(false);
          }
          return;
        }
      }

      const redirectTo = `${window.location.origin}${afterFunnelPath}`;
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: { redirectTo, queryParams: { prompt: 'select_account' } },
      });
      if (error) {
        track('fb_auth_error', { method: 'google', message: error.message || 'unknown' });
        setAuthError(getChrome(funnelLang).googleError);
        setAuthBusy(false);
      }
      // On success the browser redirects away; no further action here.
    } catch (e) {
      setAuthError(getChrome(funnelLang).googleError);
      setAuthBusy(false);
    }
  };

  const goNext = () => {
    if (step && isProgressStep(step)) {
      const raw = answers[step.key];
      // Never send the child's name (PII) as an event value; log only that it
      // was filled. All other answers are non-identifying and go through as-is.
      const value = step.providesName ? undefined : raw ?? null;
      track('fb_onb_answered', {
        key: step.key,
        value,
        filled: raw != null && raw !== '' && !(Array.isArray(raw) && raw.length === 0),
        ms_on_step: Date.now() - stepEnteredAtRef.current,
      });
    }
    if (step && step.type === 'email') {
      void finishWithEmail();
      return;
    }
    if (index >= steps.length - 1) {
      // Safety net for any non-email final step.
      completedRef.current = true;
      persistAnswers();
      if (!previewMode) navigate(afterFunnelPath);
      return;
    }
    setIndex((i) => i + 1);
  };

  const goBack = () => {
    if (index <= 0) return;
    // Going back is a confusion / second-guess signal worth seeing per question.
    track('fb_onb_back', {
      from_key: step?.key,
      to_key: steps[index - 1]?.key,
      ms_on_step: Date.now() - stepEnteredAtRef.current,
    });
    setIndex((i) => i - 1);
  };

  if (!step) return null;
  const Renderer = RENDERERS[step.type];
  if (!Renderer) return null;

  // Resolve {name} tokens for whichever fields/options this step uses.
  const resolved = {
    ...step,
    question: withName(step.question, name),
    sub: withName(step.sub, name),
    title: withName(step.title, name),
    body: withName(step.body, name),
    cta: withName(step.cta, name),
    consent: withName(step.consent, name),
    labels: step.labels ? step.labels.map((l) => withName(l, name)) : undefined,
    options: step.options
      ? step.options.map((o) => ({ ...o, label: withName(o.label, name) }))
      : undefined,
  };

  const currentProgress = isProgressStep(step)
    ? progressSteps.indexOf(step) + 1
    : totalProgress;

  return (
    <>
      <Helmet>
        <title>Timo Helps</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      {/* key=index remounts the screen so its entry animations replay per step */}
      <Renderer
        key={index}
        step={resolved}
        lang={funnelLang}
        name={name}
        score={computeScore(answers)}
        value={answers[step.key]}
        setValue={(v) => setAnswer(step.key, v)}
        consent={consent}
        setConsent={setConsent}
        onNext={goNext}
        onBack={goBack}
        onGoogle={handleGoogle}
        authBusy={authBusy}
        authError={authError}
        showBack={index > 0}
        showLogin={index === 0}
        currentStep={currentProgress}
        totalSteps={totalProgress}
      />
    </>
  );
}
